import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { useShouldHideMarketRestrictedFeatures } from '@/hooks/useShouldHideMarketRestrictedFeatures';
import { Evidence } from '@heidi/design-system/icons';
import { Region, SubscriptionTier } from '@heidi/schema/ml-scribe';
import {
  AudioLines,
  Blocks,
  BookmarkCheck,
  Check,
  FileText,
  Gem,
  Handshake,
  Infinity,
  Library,
  LibraryBig,
  LifeBuoy,
  Link,
  Lock,
  MessageSquareText,
  Sparkles,
  TextSearch,
  UsersRound,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { useIntl } from 'react-intl';

/** Lucide + design-system icons; callers often pass svg props (e.g. strokeWidth). */
export type IconComponent = ComponentType<
  SVGProps<SVGSVGElement> & { size?: number | string }
>;

export interface Feature {
  id?: string;
  icon: IconComponent;
  description?: string;
  notAvailableInRestrictedMarkets?: boolean;
  excludedRegions?: Region[];
}

export type UseFeatureListsOptions = {
  /** Overrides query user tier when computing `currentTierFeatures` (e.g. billing `currentPaidTier`). */
  currentPaidTier?: SubscriptionTier | null | undefined;
};

export const useFeatureLists = (options?: UseFeatureListsOptions) => {
  const intl = useIntl();
  const { userTier } = useQueryUserTier();
  const { shouldHide: shouldHideMarketRestrictedFeatures } =
    useShouldHideMarketRestrictedFeatures();
  const { region, isEU } = useIsUKOrEU();
  const tierForCurrentFeatures = options?.currentPaidTier ?? userTier;

  const filterMarketAvailability = (features: Feature[]) =>
    features.filter(
      (feature) =>
        !(
          shouldHideMarketRestrictedFeatures && feature.notAvailableInRestrictedMarkets
        ) &&
        !feature.excludedRegions?.some((r) => (r === Region.EU ? isEU : r === region))
    );

  const freeFeatures: Feature[] = [
    {
      icon: Blocks,
      description: intl.formatMessage({
        defaultMessage: '10 custom templates / month',
        id: 'YJ3xkdIAFr',
      }),
    },
    {
      icon: Evidence,
      description: intl.formatMessage({
        defaultMessage: '10 in-session Ask Heidi / month',
        id: '+5xlA/apfD',
      }),
      excludedRegions: [Region.EU],
    },
    {
      icon: Infinity,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Evidence',
        id: '7iTfU5YgyG',
      }),
    },
  ];

  const evidenceFeatures: Feature[] = [
    {
      icon: TextSearch,
      description: intl.formatMessage({
        defaultMessage: 'Choose which sources inform Evidence',
        id: '97/qB6dogu',
      }),
    },
    {
      icon: FileText,
      description: intl.formatMessage({
        defaultMessage: 'Access primary research and premium sources',
        id: 'vkb7xb1yW/',
      }),
    },
    {
      icon: LibraryBig,
      description: intl.formatMessage({
        defaultMessage: 'Build a personal reference library',
        id: 'RPnI8gDP6C',
      }),
    },
  ];

  /** Used in the new-user Evidence Plus trial start modal: same descriptions as `evidenceFeatures` but rendered with check icons. */
  const evidencePlusTrialStartFeatures: Feature[] = [
    {
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Choose which sources inform Evidence',
        id: '97/qB6dogu',
      }),
    },
    {
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Access primary research and premium sources',
        id: 'vkb7xb1yW/',
      }),
    },
    {
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Build a personal reference library',
        id: 'RPnI8gDP6C',
      }),
    },
  ];

  const evidenceShortFeatures: Feature[] = [
    {
      icon: Link,
      description: intl.formatMessage({
        defaultMessage: 'Choose evidence sources',
        id: 'Rmuu5xOqFb',
      }),
    },
    {
      icon: FileText,
      description: intl.formatMessage({
        defaultMessage: 'Access premium sources',
        id: 'g5YcUFEQTb',
      }),
    },
    {
      icon: LibraryBig,
      description: intl.formatMessage({
        defaultMessage: 'Build a personal reference library',
        id: 'RPnI8gDP6C',
      }),
    },
  ];

  /** Used in Clinician modal when user's current plan is Scribe Plus (Pro): evidence-style list with Build personal evidence library first (books icon). */
  const evidenceFeaturesFromPro: Feature[] = [
    {
      icon: LibraryBig,
      description: intl.formatMessage({
        defaultMessage: 'Build a personal reference library',
        id: 'RPnI8gDP6C',
      }),
    },
    {
      icon: Evidence,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Evidence in sessions',
        id: '7pAhfgIz5A',
      }),
      notAvailableInRestrictedMarkets: true,
    },
    {
      icon: TextSearch,
      description: intl.formatMessage({
        defaultMessage: 'Choose trusted evidence sources',
        id: 'IOO1qSRmrg',
      }),
    },
  ];

  const clinicianFeatures: Feature[] = [
    {
      icon: Blocks,
      description: intl.formatMessage({
        defaultMessage: 'Custom scribe templates',
        id: 'ltEPoveV6l',
      }),
    },
    {
      icon: Evidence,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Evidence in sessions',
        id: '7pAhfgIz5A',
      }),
      notAvailableInRestrictedMarkets: true,
    },
    {
      icon: TextSearch,
      description: intl.formatMessage({
        defaultMessage: 'Choose trusted evidence sources',
        id: 'IOO1qSRmrg',
      }),
    },
  ];

  /** Used in the new-user Clinician trial start modal: benefit-oriented copy with check icons. */
  const clinicianTrialStartFeatures: Feature[] = [
    {
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Create your own note templates',
        id: 'itWW1akX39',
      }),
    },
    {
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Look up clinical answers in every session',
        id: '84UkQgf4Ns',
      }),
    },
    {
      icon: Check,
      description: intl.formatMessage({
        defaultMessage: 'Choose which sources Heidi searches',
        id: 'yWFqQCJiZK',
      }),
    },
  ];

  /** Used in the Practice trial start modal (onboarding). Icons match Figma feature list. */
  const practiceTrialStartFeatures: Feature[] = [
    {
      icon: UsersRound,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited team members',
        id: 'yuDloYnS1L',
      }),
    },
    {
      icon: Blocks,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited advanced note templates',
        id: 'KTc/IA6ckQ',
      }),
    },
    {
      icon: MessageSquareText,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited evidence in patient visits',
        id: 'pbkc6ocLRL',
      }),
    },
  ];

  const clinicianShortFeatures: Feature[] = [
    {
      icon: Blocks,
      description: intl.formatMessage({
        defaultMessage: 'Custom scribe templates',
        id: 'ltEPoveV6l',
      }),
    },
    {
      icon: Evidence,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Evidence in sessions',
        id: '7pAhfgIz5A',
      }),
      notAvailableInRestrictedMarkets: true,
    },
    {
      icon: TextSearch,
      description: intl.formatMessage({
        defaultMessage: 'Choose trusted evidence sources',
        id: 'IOO1qSRmrg',
      }),
    },
  ];

  const clinicianEvidenceFeatures: Feature[] = [
    {
      icon: TextSearch,
      description: intl.formatMessage({
        defaultMessage: 'Premium research sources',
        id: '9AjNkGd0DW',
      }),
    },
    {
      icon: LibraryBig,
      description: intl.formatMessage({
        defaultMessage: 'Personal guideline library',
        id: 'IPh3b8rMLS',
      }),
    },
    {
      icon: MessageSquareText,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Ask Heidi in sessions',
        id: '9ZvO2mMcEg',
      }),
    },
  ];

  const practiceFeatures: Feature[] = [
    {
      icon: AudioLines,
      description: intl.formatMessage({
        defaultMessage: 'Session and template sharing',
        id: 'gFAwVvlf7P',
      }),
    },
    {
      icon: UsersRound,
      description: intl.formatMessage({
        defaultMessage: 'Free assistant users',
        id: '2Zv4dYcYwJ',
      }),
    },
    {
      icon: LifeBuoy,
      description: intl.formatMessage({
        defaultMessage: 'Priority support',
        id: '3sSrKzxPrc',
      }),
    },
  ];

  const practiceShortFeatures: Feature[] = [
    {
      icon: AudioLines,
      description: intl.formatMessage({
        defaultMessage: 'Session and template sharing',
        id: 'gFAwVvlf7P',
      }),
    },
    {
      icon: UsersRound,
      description: intl.formatMessage({
        defaultMessage: 'Free assistant users',
        id: '2Zv4dYcYwJ',
      }),
    },
    {
      icon: LifeBuoy,
      description: intl.formatMessage({
        defaultMessage: 'Priority support',
        id: '3sSrKzxPrc',
      }),
    },
  ];

  const practiceNewFeatures: Feature[] = [
    {
      icon: FileText,
      description: intl.formatMessage({
        defaultMessage: 'Premium research sources',
        id: '9AjNkGd0DW',
      }),
    },
    {
      icon: Library,
      description: intl.formatMessage({
        defaultMessage: 'Shared guideline library',
        id: 'VPkXXzbdNI',
      }),
    },
    {
      icon: Link,
      description: intl.formatMessage({
        defaultMessage: 'Control evidence sources',
        id: '+8TwNF+V+q',
      }),
    },
  ];

  const scribeProFeatures: Feature[] = [
    {
      icon: Blocks,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited custom templates',
        id: '6V4xTBCHGe',
      }),
    },
    {
      icon: Evidence,
      description: intl.formatMessage({
        defaultMessage: 'Unlimited Evidence',
        id: '7iTfU5YgyG',
      }),
    },
    {
      icon: Sparkles,
      description: intl.formatMessage({
        defaultMessage: 'In session suggestions',
        id: 'OtsSiBapZM',
      }),
    },
  ];

  const practiceOldFeatures: Feature[] = [
    {
      icon: Gem,
      description: intl.formatMessage({
        defaultMessage: 'Premium research sources',
        id: '9AjNkGd0DW',
      }),
    },
    {
      icon: MessageSquareText,
      description: intl.formatMessage({
        defaultMessage: '10 in-session Ask Heidi / month',
        id: '+5xlA/apfD',
      }),
      excludedRegions: [Region.EU],
    },
    {
      icon: LibraryBig,
      description: intl.formatMessage({
        defaultMessage: 'Personal guideline library',
        id: 'IPh3b8rMLS',
      }),
    },
  ];

  const practiceScribeFeatures: Feature[] = [
    {
      icon: Blocks,
      description: intl.formatMessage({
        defaultMessage: 'Full Scribe functionality with team templates',
        id: 'h/JTLt3QZc',
      }),
    },
    {
      icon: FileText,
      description: intl.formatMessage({
        defaultMessage: 'Document and session sharing',
        id: '+O6OM9Ucc0',
      }),
    },
    {
      icon: BookmarkCheck,
      description: intl.formatMessage({
        defaultMessage: 'Guided onboarding',
        id: '9PnuwagC7y',
      }),
    },
  ];

  const evidenceTeamFeatures: Feature[] = [
    {
      icon: Library,
      description: intl.formatMessage({
        defaultMessage: 'Shared evidence library and guidelines',
        id: 'x4/lo1cbbo',
      }),
    },
    {
      icon: UsersRound,
      description: intl.formatMessage({
        defaultMessage: 'Team management and centralized billing',
        id: 'AQusvosN6s',
      }),
    },
    {
      icon: LifeBuoy,
      description: intl.formatMessage({
        defaultMessage: 'Priority support',
        id: '3sSrKzxPrc',
      }),
    },
  ];

  const enterpriseFeatures: Feature[] = [
    {
      icon: Lock,
      description: intl.formatMessage({
        defaultMessage: 'SSO and enterprise-grade governance',
        id: 'q2pMOwbr/Z',
      }),
    },
    {
      icon: LifeBuoy,
      description: intl.formatMessage({
        defaultMessage: 'Dedicated customer success',
        id: 'WaFkekOpJn',
      }),
    },
    {
      icon: Handshake,
      description: intl.formatMessage({
        defaultMessage: 'Service commitments and custom hosting',
        id: 'wUw1594WHY',
      }),
    },
  ];

  const currentTierFeatures = (() => {
    switch (tierForCurrentFeatures) {
      case SubscriptionTier.PRO:
        return scribeProFeatures;
      case SubscriptionTier.EVIDENCE:
        return evidenceShortFeatures;
      case SubscriptionTier.CLINICIAN_PRO:
        return clinicianEvidenceFeatures;
      case SubscriptionTier.CLINICIAN_PRO_PLUS:
        return practiceFeatures;
      case SubscriptionTier.EVIDENCE_TEAM:
        return evidenceTeamFeatures;
      case SubscriptionTier.ENTERPRISE:
      case SubscriptionTier.CLINICIAN_ENTERPRISE:
        return enterpriseFeatures;
      case SubscriptionTier.PRO_PLUS:
        return practiceScribeFeatures;
      default:
        return scribeProFeatures;
    }
  })();

  return {
    freeFeatures: filterMarketAvailability(freeFeatures),
    evidenceFeatures: filterMarketAvailability(evidenceFeatures),
    evidencePlusTrialStartFeatures: filterMarketAvailability(
      evidencePlusTrialStartFeatures
    ),
    evidenceShortFeatures: filterMarketAvailability(evidenceShortFeatures),
    evidenceFeaturesFromPro: filterMarketAvailability(evidenceFeaturesFromPro),
    clinicianFeatures: filterMarketAvailability(clinicianFeatures),
    clinicianTrialStartFeatures: filterMarketAvailability(clinicianTrialStartFeatures),
    clinicianShortFeatures: filterMarketAvailability(clinicianShortFeatures),
    clinicianEvidenceFeatures: filterMarketAvailability(clinicianEvidenceFeatures),
    practiceTrialStartFeatures: filterMarketAvailability(practiceTrialStartFeatures),
    practiceFeatures: filterMarketAvailability(practiceFeatures),
    practiceShortFeatures: filterMarketAvailability(practiceShortFeatures),
    practiceNewFeatures: filterMarketAvailability(practiceNewFeatures),
    scribeProFeatures: filterMarketAvailability(scribeProFeatures),
    practiceOldFeatures: filterMarketAvailability(practiceOldFeatures),
    evidenceTeamFeatures: filterMarketAvailability(evidenceTeamFeatures),
    enterpriseFeatures: filterMarketAvailability(enterpriseFeatures),
    practiceScribeFeatures: filterMarketAvailability(practiceScribeFeatures),
    currentTierFeatures: filterMarketAvailability(currentTierFeatures),
  };
};
